/**
 * UserJourney Ranking - Shared logic for selecting the best UserJourney config
 *
 * Uses tuple-based ranking [orgScore, versionScore, roleScore]:
 * - Exact match (2) > wildcard (1) > no match (-1 = disqualified)
 * - Tie-break by createdAt desc (newest wins)
 *
 * Used by:
 * - Server: mongo/index.ts (UserJourneyCollection.findByPolicyIdentifier)
 * - Server: resolvers/user-journey.ts (query resolver)
 */

// Plain interface — no Mongoose dependency, keeps this module isomorphic
export interface UserJourneyCandidate {
  _id: string;
  policyVersions?: string[] | null;
  roles?: string[] | null;
  orgId: string;
  createdAt?: Date | number;
}

/**
 * Normalize an array field from a UserJourney document.
 * Handles malformed data: null, undefined, non-array, arrays with non-string elements.
 * Returns ['*'] (wildcard) for any invalid/empty input.
 */
export function normalizeArray(val: unknown): string[] {
  if (!Array.isArray(val) || val.length === 0) {
    return ['*'];
  }
  const filtered = val.filter(
    (v): v is string => typeof v === 'string' && v.length > 0
  );
  return filtered.length === 0 ? ['*'] : filtered;
}

/**
 * Check if two arrays have any overlapping elements.
 * Treats ['*'] as overlapping with everything.
 */
export function hasArrayOverlap(a: string[], b: string[]): boolean {
  if (a.includes('*') || b.includes('*')) {
    return true;
  }
  const setB = new Set(b);
  return a.some((item) => setB.has(item));
}

type RankTuple = [number, number, number];

function rankCandidate(
  doc: UserJourneyCandidate,
  requestedVersion: string | undefined,
  requestedRole: string | undefined,
  requestedOrgId: string | undefined
): RankTuple | null {
  const versions = normalizeArray(doc.policyVersions);
  const roles = normalizeArray(doc.roles);

  // Version: exact match (2) > wildcard (1) > no match (-1 = disqualify)
  const versionScore =
    requestedVersion && versions.includes(requestedVersion)
      ? 2
      : versions.includes('*')
        ? 1
        : -1;
  if (versionScore < 0) {
    return null;
  }

  // Role: exact match (2) > wildcard (1) > no match (-1 = disqualify)
  const roleScore =
    requestedRole && roles.includes(requestedRole)
      ? 2
      : roles.includes('*')
        ? 1
        : -1;
  if (roleScore < 0) {
    return null;
  }

  // Org: exact match (2) > SYSTEM fallback (1)
  const orgScore = doc.orgId === requestedOrgId ? 2 : 1;

  return [orgScore, versionScore, roleScore];
}

function compareTuples(a: RankTuple, b: RankTuple): number {
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) {
      return b[i] - a[i]; // descending
    }
  }
  return 0; // equal
}

/**
 * Find the best matching UserJourney config from a list of candidates.
 *
 * Ranking: [orgScore, versionScore, roleScore] descending.
 * Tie-break by createdAt desc (newest wins).
 * Returns null if no valid candidate (caller should fail-open).
 */
export function findBestUserJourney<T extends UserJourneyCandidate>(
  candidates: T[],
  requestedVersion?: string,
  requestedRole?: string,
  requestedOrgId?: string
): T | null {
  if (candidates.length === 0) {
    return null;
  }

  let bestDoc: T | null = null;
  let bestRank: RankTuple | null = null;

  for (const doc of candidates) {
    const rank = rankCandidate(
      doc,
      requestedVersion,
      requestedRole,
      requestedOrgId
    );
    if (!rank) {
      continue; // disqualified
    }

    if (!bestRank) {
      bestDoc = doc;
      bestRank = rank;
      continue;
    }

    const cmp = compareTuples(rank, bestRank);
    if (cmp < 0) {
      // rank is better (lower comparison = higher priority in descending sort)
      bestDoc = doc;
      bestRank = rank;
    } else if (cmp === 0) {
      // tie — pick newest by createdAt
      const docTime = doc.createdAt
        ? new Date(doc.createdAt as any).getTime()
        : 0;
      const bestTime = bestDoc?.createdAt
        ? new Date(bestDoc.createdAt as any).getTime()
        : 0;
      if (docTime > bestTime) {
        bestDoc = doc;
        bestRank = rank;
      }
    }
  }

  return bestDoc;
}
